import { supabase } from './supabaseClient';
import { logger } from '../utils/logger';

export interface StoryImage {
  id: string;
  story_id: string;
  position: number;
  image_path: string;
  prompt?: string;
  caption?: string;
  created_at: string;
}

export interface ImageGenerationStatus {
  has_images: boolean;
  images_status: 'none' | 'pending' | 'completed' | 'failed';
}

export const StoryImageService = {
  // Request image generation for a story
  requestImageGeneration: async (storyId: string, storyText: string, characters: string[], length: string): Promise<boolean> => {
    try {
      logger.log(`Requesting image generation for story ${storyId}`);
      
      // Get user session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('You must be logged in to generate images');
      }
      
      // Update story status to pending
      const { error: updateError } = await supabase
        .from('stories')
        .update({ images_status: 'pending' })
        .eq('id', storyId);
        
      if (updateError) {
        throw new Error(`Failed to update story status: ${updateError.message}`);
      }
      
      // Call Edge Function to start image generation process
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/story-images`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.session.access_token}`
        },
        body: JSON.stringify({
          storyId,
          storyText,
          characters,
          length
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Image generation request failed: ${errorData.error || response.statusText}`);
      }
      
      return true;
    } catch (error) {
      logger.error('Error requesting image generation:', error);
      // Update story status to failed if the request fails
      await supabase
        .from('stories')
        .update({ images_status: 'failed' })
        .eq('id', storyId);
      throw error;
    }
  },
  
  // Get image generation status for a story
  getImageGenerationStatus: async (storyId: string): Promise<ImageGenerationStatus> => {
    try {
      logger.log(`Checking image generation status for story ${storyId}`);
      
      const { data, error } = await supabase
        .from('stories')
        .select('has_images, images_status')
        .eq('id', storyId)
        .single();
        
      if (error) {
        throw new Error(`Failed to get image status: ${error.message}`);
      }
      
      return {
        has_images: data.has_images || false,
        images_status: data.images_status || 'none'
      };
    } catch (error) {
      logger.error('Error getting image generation status:', error);
      return { has_images: false, images_status: 'none' };
    }
  },
  
  // Get images for a story
  getStoryImages: async (storyId: string): Promise<StoryImage[]> => {
    try {
      logger.log(`Fetching images for story ${storyId}`);
      
      const { data, error } = await supabase
        .from('story_images')
        .select('*')
        .eq('story_id', storyId)
        .order('position');
        
      if (error) {
        throw new Error(`Failed to fetch story images: ${error.message}`);
      }
      
      return data || [];
    } catch (error) {
      logger.error('Error fetching story images:', error);
      return [];
    }
  },
  
  // Subscribe to image status changes
  subscribeToImageUpdates: (storyId: string, callback: (status: ImageGenerationStatus) => void) => {
    const channel = supabase
      .channel(`story-${storyId}`)
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'stories', 
        filter: `id=eq.${storyId}` 
      }, (payload) => {
        // When the story is updated (likely image status changed)
        callback({
          has_images: payload.new.has_images || false,
          images_status: payload.new.images_status || 'none'
        });
      })
      .subscribe();
      
    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  },
  
  // Delete images for a story (called when a story is deleted)
  deleteStoryImages: async (storyId: string): Promise<void> => {
    try {
      logger.log(`Deleting images for story ${storyId}`);
      
      // Get all images for this story
      const { data: images, error: fetchError } = await supabase
        .from('story_images')
        .select('image_path')
        .eq('story_id', storyId);
        
      if (fetchError) {
        throw new Error(`Failed to fetch images to delete: ${fetchError.message}`);
      }
      
      // Delete from storage
      if (images && images.length > 0) {
        const filesToDelete = images.map(img => {
          // Extract filename from URL or path
          const path = img.image_path.split('/').slice(-2).join('/');
          return path;
        });
        
        const { error: storageError } = await supabase.storage
          .from('story-images')
          .remove(filesToDelete);
          
        if (storageError) {
          logger.error('Error deleting image files:', storageError);
        }
      }
      
      // Note: We don't need to delete from the database as it will cascade
      
    } catch (error) {
      logger.error('Error deleting story images:', error);
      throw error;
    }
  }
}; 